import { useState } from "react";
import { MessageSquareWarning, Star, Info } from "lucide-react";

// Canonical keys match backend models.OWNER_CONCERN_CATEGORIES.
const CATEGORIES = [
  { k: "declining_sales", label: "Declining sales" },
  { k: "weak_customer_acquisition", label: "Weak customer acquisition" },
  { k: "customer_retention", label: "Customer retention" },
  { k: "marketing_efficiency", label: "Marketing efficiency" },
  { k: "social_media_performance", label: "Social media performance" },
  { k: "customer_complaints", label: "Customer complaints" },
  { k: "reputation", label: "Reputation" },
  { k: "competitor_pressure", label: "Competitor pressure" },
  { k: "staffing", label: "Staffing" },
  { k: "labor_costs", label: "Labor costs" },
  { k: "operating_expenses", label: "Operating expenses" },
  { k: "pricing_margins", label: "Pricing & margins" },
  { k: "cash_flow", label: "Cash flow" },
  { k: "inventory", label: "Inventory" },
  { k: "operational_processes", label: "Operational processes" },
  { k: "expansion_planning", label: "Expansion planning" },
  { k: "other", label: "Other" },
];

const textarea = "w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/40 focus:border-indigo-400";

export function OwnerConcernsInput({ onChange }) {
  const [categories, setCategories] = useState([]);
  const [priority, setPriority] = useState(null);
  const [details, setDetails] = useState("");
  const [goals, setGoals] = useState("");

  const emit = (next) => {
    const state = { categories, priority_category: priority, details, goals, ...next };
    onChange && onChange({
      categories: state.categories,
      priority_category: state.priority_category,
      details: state.details,
      goals: state.goals,
    });
  };

  const toggle = (k) => {
    const has = categories.includes(k);
    const next = has ? categories.filter((c) => c !== k) : [...categories, k];
    const nextPriority = has && priority === k ? null : priority;
    setCategories(next);
    setPriority(nextPriority);
    emit({ categories: next, priority_category: nextPriority });
  };

  const markPriority = (k) => {
    const next = priority === k ? null : k;
    setPriority(next);
    // selecting a priority also selects the category
    const cats = categories.includes(k) ? categories : [...categories, k];
    setCategories(cats);
    emit({ priority_category: next, categories: cats });
  };

  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm" data-testid="owner-concerns-input">
      <div className="flex items-center gap-2.5 mb-1.5">
        <span className="grid place-items-center w-8 h-8 rounded-lg bg-amber-50 text-amber-600">
          <MessageSquareWarning className="w-4 h-4" />
        </span>
        <h3 className="font-heading font-bold text-slate-900">Tell us what's holding your business back</h3>
      </div>
      <p className="text-sm text-slate-600 mb-4">Optional — in your own words. We treat this as your perspective and cross-check it against what the scan finds. We never present your input as a verified fact.</p>

      <div className="flex flex-wrap gap-2" data-testid="owner-concerns-categories">
        {CATEGORIES.map((c) => {
          const active = categories.includes(c.k);
          const isPrio = priority === c.k;
          return (
            <div key={c.k} className="inline-flex items-center">
              <button type="button" onClick={() => toggle(c.k)} data-testid={`concern-cat-${c.k}`}
                className={`rounded-l-full border px-3 py-1.5 text-xs font-medium transition-colors ${active ? "border-indigo-500 bg-indigo-50 text-indigo-700" : "border-slate-200 text-slate-600 hover:border-slate-300"}`}>
                {c.label}
              </button>
              <button type="button" onClick={() => markPriority(c.k)} aria-label={`Mark ${c.label} as top priority`}
                data-testid={`concern-priority-${c.k}`}
                className={`rounded-r-full border border-l-0 px-2 py-1.5 transition-colors ${isPrio ? "border-amber-400 bg-amber-100 text-amber-600" : active ? "border-indigo-500 bg-indigo-50 text-slate-400 hover:text-amber-500" : "border-slate-200 text-slate-300 hover:text-amber-400"}`}>
                <Star className={`w-3.5 h-3.5 ${isPrio ? "fill-amber-500" : ""}`} />
              </button>
            </div>
          );
        })}
      </div>
      {priority && (
        <p className="mt-2 text-[11px] text-amber-600 flex items-center gap-1" data-testid="owner-concerns-priority-note">
          <Star className="w-3 h-3 fill-amber-500" /> Marked as your #1 concern
        </p>
      )}

      <label className="block mt-4">
        <span className="text-[12px] font-medium text-slate-600">What's the biggest thing getting in the way? (optional)</span>
        <textarea rows={3} className={`${textarea} mt-1`} value={details} maxLength={4000}
          onChange={(e) => { setDetails(e.target.value); emit({ details: e.target.value }); }}
          placeholder="e.g. Sales have dropped since a competitor opened nearby and we're not sure why…"
          data-testid="owner-concerns-details" />
      </label>

      <label className="block mt-3">
        <span className="text-[12px] font-medium text-slate-600">What would success look like? (optional)</span>
        <input className={`${textarea} mt-1`} value={goals} maxLength={2000}
          onChange={(e) => { setGoals(e.target.value); emit({ goals: e.target.value }); }}
          placeholder="e.g. Get back to last year's revenue within 6 months"
          data-testid="owner-concerns-goals" />
      </label>

      <div className="mt-3 flex items-start gap-2 rounded-lg bg-slate-50 border border-slate-200 px-3 py-2 text-[12px] text-slate-500">
        <Info className="w-4 h-4 shrink-0 mt-0.5" />
        Your answers stay private to your report. We use them to focus the analysis and recommendations — not as proof of a problem.
      </div>
    </div>
  );
}

export default OwnerConcernsInput;
